import React, { useState } from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore from "swiper";
import VideoModal from "./videoModal";
import { AbsoluteWrapper } from "@util/standard";
import { CircleArrow } from "@util/assets";

interface VideoItem {
  video: string;
  description: string;
}

interface Props {
  data: {
    _key: string;
    _type: "videoCarousel";
    marginBottom?: string;
    title?: string;
    carousel?: VideoItem[];
  };
}

const VideoCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  gap: 40px;
  background-color: #1b1b1c0d;
  @media screen and (max-width: 1024px) {
    padding: 60px 20px;
  }
  @media screen and (max-width: 767px) {
    padding: 60px 0px 60px 20px;
  }
  @media screen and (max-width: 576px) {
    padding: 60px 20px;
  }
  .carousel-item {
    display: flex;
    flex-direction: column;
    gap: 40px;
    @media screen and (max-width: 767px) {
      gap: 20px;
    }
    p {
      text-align: center;
      @media screen and (max-width: 767px) {
        text-align: left;
      }
    }
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 60px;
  line-height: 1.1;
`;

const NavWrapper = styled(AbsoluteWrapper)`
  display: flex;
  cursor: pointer;
  z-index: 3;
  top: 34%;
  transform: translateY(-34%);
  @media screen and (max-width: 1440px) {
    top: 30.5%;
    transform: translateY(-30.5%);
  }
  @media screen and (max-width: 1024px) {
    display: none;
  }

  svg {
    height: 50px;
    width: 50px;
  }
`;
const StyledSwiper = styled(Swiper)`
  width: 100%;
  max-width: 1440px;
  .swiper-slide {
    cursor: pointer;
  }
`;

// Add pagination dots styling
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
`;

const PaginationDot = styled.div<{ isActive: boolean }>`
  width: ${props => (props.isActive ? "45px" : "15px")};
  height: 15px;
  border-radius: ${props => (props.isActive ? "20px" : "50%")};
  background-color: ${props => (props.isActive ? "#D6001C " : "#ccc")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => (props.isActive ? "#D6001C " : "#999")};
  }
`;

function VideoCarousel({ data }: Props) {
  if (!data || !data.carousel || data.carousel.length === 0) return null;
  const { title, carousel } = data;
  const [controller, setController] = useState<SwiperCore>();
  const [activeIndex, setActiveIndex] = useState(0);

  const slidePrev = () => {
    controller?.slidePrev();
  };
  const slideNext = () => {
    controller?.slideNext();
  };

  // New function to handle dot click
  const handleDotClick = (index: number) => {
    controller?.slideToLoop(index);
  };

  return (
    <VideoCarouselContainer>
      {title && <Title>{title}</Title>}

      <StyledSwiper
        slidesPerView={3}
        loop
        spaceBetween={20}
        loopedSlides={carousel.length}
        centeredSlides={true}
        onSwiper={setController}
        onSlideChange={swiper => setActiveIndex(swiper.realIndex)}
        breakpoints={{
          0: {
            slidesPerView: 1,
          },
          576: {
            slidesPerView: 1.2,
            centeredSlides: false,
          },
          768: {
            slidesPerView: 2,
            centeredSlides: false,
          },
          1024: {
            slidesPerView: 2.5,
            centeredSlides: true,
          },
          1440: {
            slidesPerView: 3,
            centeredSlides: true,
          },
        }}
      >
        <NavWrapper left="5%" onClick={slidePrev}>
          <CircleArrow height="50px" width="50px" />
        </NavWrapper>
        <NavWrapper right="5%" onClick={slideNext}>
          <CircleArrow flip height="50px" width="50px" />
        </NavWrapper>
        {carousel.map((item, index) => {
          return (
            <SwiperSlide key={`swiper_${index}`}>
              <div className="carousel-item">
                <VideoModal video={item.video} isActive={index === activeIndex} />
                <p>{item.description}</p>
              </div>
            </SwiperSlide>
          );
        })}
      </StyledSwiper>

      {/* Add pagination dots */}
      <PaginationContainer>
        {carousel.map((_, index) => (
          <PaginationDot
            key={`dot_${index}`}
            isActive={index === activeIndex}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </PaginationContainer>
    </VideoCarouselContainer>
  );
}

export default VideoCarousel;
